import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js'
import type { SupabaseClient } from '@supabase/supabase-js'

export type AlertActionType = 
  | 'internal_navigation'
  | 'external_link'
  | 'modal'
  | 'function'
  | 'api_call'

export interface AlertAction {
  type: AlertActionType
  target: string
  label: string
}

export interface UserAlert {
  id: string
  user_id: string
  company_id: string
  title: string
  message: string
  source: string
  priority: 'low' | 'normal' | 'high' | 'urgent'
  read_at: string | null
  created_at: string
  updated_at: string
  actions: AlertAction[]
  metadata: Record<string, any>
  agent_task_execution_id: string | null
}

export const useUserAlerts = () => useState<{
  alerts: UserAlert[]
  isLoading: boolean
  error: string | null
  currentCompanyId: string | null
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}>('userAlerts', () => ({
  alerts: [],
  isLoading: false,
  error: null,
  currentCompanyId: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  }
}))

export async function fetchUserAlerts(companyId: string, page: number = 1, limit: number = 10) {
  console.log("Fetching user alerts for company:", companyId, "page:", page, "limit:", limit)
  const { $supabase } = useNuxtApp()
  const supabase = $supabase as SupabaseClient
  const userAlerts = useUserAlerts()

  userAlerts.value.isLoading = true
  userAlerts.value.error = null
  userAlerts.value.currentCompanyId = companyId

  try {
    const offset = (page - 1) * limit

    const { data, error, count } = await supabase
      .from('user_alerts')
      .select('*', { count: 'exact' })
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1)

    if (error) {
      console.error("Error fetching user alerts:", error)
      throw error
    }

    console.log("Fetched user alerts:", data, "total count:", count)
    
    // If this is page 1, replace the alerts array, otherwise append
    if (page === 1) {
      userAlerts.value.alerts = data as UserAlert[]
    } else {
      userAlerts.value.alerts = [...userAlerts.value.alerts, ...(data as UserAlert[])]
    }

    // Update pagination metadata
    userAlerts.value.pagination = {
      page,
      limit,
      total: count || 0,
      totalPages: Math.ceil((count || 0) / limit),
      hasNextPage: (page * limit) < (count || 0),
      hasPreviousPage: page > 1
    }
  } catch (err) {
    console.error('Error in fetchUserAlerts:', err)
    userAlerts.value.error = 'Failed to load alerts'
  } finally {
    userAlerts.value.isLoading = false
  }
}

export async function loadMoreAlerts() {
  const userAlerts = useUserAlerts()
  
  if (!userAlerts.value.currentCompanyId || !userAlerts.value.pagination.hasNextPage) {
    return
  }

  const nextPage = userAlerts.value.pagination.page + 1
  await fetchUserAlerts(userAlerts.value.currentCompanyId, nextPage, userAlerts.value.pagination.limit)
}

export async function handleRealtimeUserAlertUpdate(payload: RealtimePostgresChangesPayload<UserAlert>) {
  console.log("Realtime user alert update received:", payload)
  const userAlerts = useUserAlerts()
  const { eventType, new: newRecord, old: oldRecord } = payload

  if (userAlerts.value.currentCompanyId) {
    const isRelevantAlert = (alert: Partial<UserAlert> | null): alert is UserAlert => 
      alert?.company_id === userAlerts.value.currentCompanyId && 
      typeof alert?.id === 'string'

    switch (eventType) {
      case 'INSERT':
        if (newRecord && isRelevantAlert(newRecord)) {
          userAlerts.value.alerts = [newRecord, ...userAlerts.value.alerts]
        }
        break

      case 'UPDATE':
        if (newRecord && isRelevantAlert(newRecord)) {
          userAlerts.value.alerts = userAlerts.value.alerts.map(alert => 
            alert.id === newRecord.id ? newRecord : alert
          )
        }
        break

      case 'DELETE':
        if (oldRecord?.id) {
          userAlerts.value.alerts = userAlerts.value.alerts.filter(alert => 
            alert.id !== oldRecord.id
          )
        }
        break
    }
  }
}

export async function markAlertAsRead(alertId: string) {
  console.log("Marking alert as read:", alertId)
  const { $supabase } = useNuxtApp()
  const supabase = $supabase as SupabaseClient
  const userAlerts = useUserAlerts()

  try {
    const { error } = await supabase
      .from('user_alerts')
      .update({ read_at: new Date().toISOString() })
      .eq('id', alertId)
      .eq('company_id', userAlerts.value.currentCompanyId)

    if (error) {
      console.error("Error marking alert as read:", error)
      throw error
    }
  } catch (err) {
    console.error('Error in markAlertAsRead:', err)
    throw err
  }
}

export async function markAllAlertsAsRead() {
  console.log("Marking all alerts as read")
  const { $supabase } = useNuxtApp()
  const supabase = $supabase as SupabaseClient
  const userAlerts = useUserAlerts()

  try {
    const { error } = await supabase
      .from('user_alerts')
      .update({ read_at: new Date().toISOString() })
      .is('read_at', null)
      .eq('company_id', userAlerts.value.currentCompanyId)

    if (error) {
      console.error("Error marking all alerts as read:", error)
      throw error
    }
  } catch (err) {
    console.error('Error in markAllAlertsAsRead:', err)
    throw err
  }
}

export async function deleteAlert(alertId: string) {
  console.log("Deleting alert:", alertId)
  const { $supabase } = useNuxtApp()
  const supabase = $supabase as SupabaseClient
  const userAlerts = useUserAlerts()

  try {
    const { error } = await supabase
      .from('user_alerts')
      .delete()
      .eq('id', alertId)
      .eq('company_id', userAlerts.value.currentCompanyId)

    if (error) {
      console.error("Error deleting alert:", error)
      throw error
    }
  } catch (err) {
    console.error('Error in deleteAlert:', err)
    throw err
  }
} 